//@flow
import * as React from 'react';
import { DEFAULT_COLUMNS } from 'common/entity/riderMaster/RiderMasterContainer';
import IncrementalPjpDataSelectionTable from './IncrementalPjpDataSelectionTable';
import { FilterBuilder } from 'common/models/internal/Filter';
import { IncludeTypeEnums } from 'common/enums/EntityMasterEnum';
import { useSelector } from 'react-redux';
import { clientSelector } from 'common/redux/selectors/queryParamsSelectors';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import PjpActions from 'common/redux/actions/pjpMaster';
import { MasterRider } from 'common/models/internal/MasterRider';
import { useTranslation } from 'react-i18next';
import { getEntitySettings } from 'common/redux/selectors/clientSelector';
import { IncrementalPjpContext } from './IncrementalPjpContext';
import { withUnitConverter } from 'common/containers/intl';
import type UnitConversionHelper from 'common/helpers/UnitConversionHelper';

type Props = {
    title: string,
    onNext: ({
        selectedRows: Array<MasterRider>,
        existingRows: Array<MasterRider>
    }) => void,
    customActions: {
        onNext: ({}, transitionOnly: boolean) => void,
        onCancel: () => void
    },
    errorMessage: string,
    match: $FixMe,
    location: $FixMe,
    getExtraFiltersForFetchData: () => Array<FilterBuilder>,
    unitConverter: UnitConversionHelper
};

const IncrementalPjpRiderContainer = (props: Props): React.Node => {
    const clientId = useSelector(state => clientSelector(state, props));
    const { getExtraFiltersForFetchData } = props;

    const riderApiFilters = React.useMemo(() => {
        const statusFilter = new FilterBuilder()
            .withName('status')
            .withOperation('EQUALS')
            .withValue('ACTIVE');

        return new FilterListBuilder()
            .addAll([statusFilter, ...getExtraFiltersForFetchData()])
            .build();
    }, [getExtraFiltersForFetchData]);

    const getNewRiders = React.useCallback(
        ({
            allExistingRecords
        }: {
            allExistingRecords: Array<MasterRider>
        }) => {
            const params = {
                clientId,
                include: [
                    IncludeTypeEnums.Shift,
                    IncludeTypeEnums.SalesCategories,
                    IncludeTypeEnums.RiderPersona
                ]
            };
            const body = {
                page: 1,
                size: 100,
                sortingInfo: [],
                filters: riderApiFilters
            };
            const options = {};
            return PjpActions.fetchAllActiveRiders(params, body, options).then(
                ({ entities }) => {
                    const existingRiderIds = allExistingRecords.map(
                        rider => rider.id
                    );
                    const filteredNewRiders = entities.entities.filter(
                        rider => existingRiderIds.indexOf(rider.id) === -1
                    );
                    return filteredNewRiders;
                }
            );
        },
        [clientId, riderApiFilters]
    );

    const getExistingEntity = React.useCallback(() => {
        const params = {
            clientId,
            pjpId: props.match?.params.pjpId,
            include: [
                IncludeTypeEnums.Shift,
                IncludeTypeEnums.SalesCategories,
                IncludeTypeEnums.RiderPersona
            ]
        };
        const body = {};
        const options = {};
        return PjpActions.fetchPjpRidersCall(params, body, options).then(
            ({ entities }) => entities.riders
        );
    }, [clientId, props.match.params.pjpId]);

    const { t } = useTranslation('entityManagement');

    const entitySettings = useSelector(state =>
        getEntitySettings(state, props)
    );

    const areFieldsHidden = React.useCallback(
        property => {
            if (property === 'riderPersona') {
                return !entitySettings.riderPersonaSettings.enabled;
            }
            if (property === 'pjp') {
                return !entitySettings.pjpSettings.enabled;
            }
            return false;
        },
        [
            entitySettings.pjpSettings.enabled,
            entitySettings.riderPersonaSettings.enabled
        ]
    );

    const { config, onRidersNext } = React.useContext(IncrementalPjpContext);

    const _onRidersNext = React.useCallback(
        selectedRoes => {
            props.customActions.onNext({}, true);
            onRidersNext(selectedRoes);
        },
        [onRidersNext, props]
    );

    return (
        <IncrementalPjpDataSelectionTable
            {...props}
            onNext={_onRidersNext}
            key="rider"
            getNewEntity={getNewRiders}
            getExistingEntity={getExistingEntity}
            EntityModel={MasterRider}
            filters={riderApiFilters}
            defaultColumns={DEFAULT_COLUMNS}
            entitySettingsName="riderSettings"
            existingTabTitle={t('pjpCreate.incrementalPlan.existingRiders')}
            newTabTitle={t('pjpCreate.incrementalPlan.newRiders')}
            isFieldHidden={areFieldsHidden}
            allowExistingSelection={config.oldRidersSelection}
            allowNewSelection={config.newRidersSelection}
        />
    );
};

export default withUnitConverter(IncrementalPjpRiderContainer);
